import { zodResolver } from '@hookform/resolvers/zod';
import { loginInputSchema, type LoginInput } from '@idpe/schema';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { Button } from '~/components/ui/Button.js';
import { Field } from '~/components/ui/Field.js';
import { useLogin } from '~/features/auth/hooks.js';
import { ApiError } from '~/lib/api.js';

export const Route = createFileRoute('/login')({
  /** Se já logado, redireciona para /dashboard antes de renderizar o form. */
  beforeLoad: async () => {
    const res = await fetch('/api/auth/me', { credentials: 'include' });
    if (res.ok) {
      throw redirect({ to: '/dashboard' });
    }
  },
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const login = useLogin();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginInputSchema),
    defaultValues: { email: '', password: '' },
  });

  async function onSubmit(values: LoginInput) {
    try {
      await login.mutateAsync(values);
      toast.success('Bem-vindo de volta.');
      navigate({ to: '/dashboard' });
    } catch (err) {
      if (err instanceof ApiError && err.is(401)) {
        toast.error('E-mail ou senha incorretos.');
      } else {
        toast.error(err instanceof ApiError ? err.displayMessage : 'Erro inesperado.');
      }
    }
  }

  return (
    <main className="login-shell">
      <div className="login-card">
        <p className="login-eyebrow">/ Painel administrativo</p>
        <h1 className="login-title">IDPE</h1>
        <p className="login-subtitle">Entre para gerenciar o conteúdo do site.</p>

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Field
            label="E-mail"
            type="email"
            autoComplete="email"
            inputMode="email"
            error={errors.email?.message}
            required
            {...register('email')}
          />
          <Field
            label="Senha"
            type="password"
            autoComplete="current-password"
            error={errors.password?.message}
            required
            {...register('password')}
          />
          <Button type="submit" variant="primary" loading={isSubmitting} fullWidth>
            Entrar
          </Button>
        </form>
      </div>
    </main>
  );
}
